import type { Editor } from "@tldraw/tldraw";

// Matches any QR code shape on the page (kept in sync with the QR shape detection
// used across the QR panels).
const isQrShape = (shape: any) =>
  shape.id.includes("qrCode") ||
  (shape.type === "customImage" && shape.props?.qrCodeLink) ||
  (shape.type === "customImage" && shape.props?.assetId?.toString().includes("qr"));

/**
 * Clears the link from every QR code shape and re-renders its image to the default
 * placeholder QR (encodes "oppizi.com"), matching a freshly-added QR.
 *
 * Used when the user empties the QR link field: the link is removed everywhere and
 * the image falls back to the default instead of keeping the previous (now stale) QR.
 */
export async function resetQrShapesToDefault(editor: Editor): Promise<void> {
  const qrShapes = editor.getCurrentPageShapes().filter(isQrShape);
  if (qrShapes.length === 0) return;

  // Clear the link first (synchronously) so the shape-sync effects can't restore it, and collect
  // each QR asset's current colors so the default placeholder keeps the user's chosen styling.
  const assetColors = new Map<string, { dark: string; light: string }>();
  qrShapes.forEach((shape: any) => {
    const assetId = shape.props?.assetId;
    if (assetId && !assetColors.has(assetId)) {
      assetColors.set(assetId, {
        dark: shape.props?.qrPixelColor || "#000000",
        light: shape.props?.qrBackgroundColor || "#FFFFFF",
      });
    }
    editor.updateShape({ ...shape, props: { ...shape.props, qrCodeLink: "" } });
  });

  try {
    const QRCode = (await import("qrcode")).default;
    const updates = await Promise.all(
      Array.from(assetColors.entries()).map(async ([id, { dark, light }]) => {
        const svg = await QRCode.toString("oppizi.com", {
          type: "svg",
          margin: 0,
          width: 300,
          color: { dark, light },
        });
        return {
          id: id as any,
          type: "image" as const,
          typeName: "asset" as const,
          meta: {},
          props: {
            name: "image",
            src: `data:image/svg+xml;base64,${btoa(svg)}`,
            w: 150,
            h: 150,
            mimeType: "image/svg+xml",
            isAnimated: false,
          },
        };
      })
    );

    if (updates.length > 0) {
      editor.updateAssets(updates as any);
    }
  } catch {
    // Best-effort: the link is already cleared; if QR generation fails we just leave the old image.
  }
}
